import React, { useEffect } from 'react';
import { colors } from '../styles/colors';
import { fonts, fontWeights } from '../styles/fonts';

function TermsOfUse({ onClose }) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.backdrop} onClick={handleBackdropClick}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Terms of Use</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          <p style={styles.introText}>
            Please carefully read these terms of use (the "Terms of Use") before using any FOI Group, LLC ("FOIG") affiliated website (the "Website"). Your access to, and use of, this Website and the material contained herein is conditioned upon your acceptance and compliance with these Terms of Use. If you do not accept these Terms of Use, please leave this Website immediately and cease any further use of any material that you may have obtained herein.
          </p>

          <h3 style={styles.sectionTitle}>Copyright and Usage of Content</h3>
          <p style={styles.text}>
            The copyrights and other rights to materials on this Website are owned by FOIG and its subsidiaries and affiliates. You are only authorized to view, download, and reproduce the materials contained herein provided that you do the following: (1) retain all notices contained within the original material; (2) only use images with surrounding text relating to such images; and (3) include the following copyright notice:
          </p>
          <p style={styles.copyrightNotice}>
            © FOI Group, LLC 2025. All rights reserved.
          </p>
          <p style={styles.text}>
            You may make no further publication or commercial use of the materials contained on this Website without the express written consent of FOIG.
          </p>
          <p style={styles.text}>
            You may not disclose or make any part of this Website available on a third-party website whether by hyperlink, framing on the internet, or otherwise without FOIG's prior written consent. This Website and its content may not be used to construct a database of any kind nor may such be stored (in whole or part) in databases for access by you or any third party or to distribute any database containing all or part of this Website or its content.
          </p>

          <h3 style={styles.sectionTitle}>Other Terms and Notices May Apply</h3>
          <p style={styles.text}>
            Additional terms and conditions may apply to business transacted or promulgated by FOIG through this Website. Additional terms may govern bulletin board services, chat areas, or other message or communication channels offered through this Website. Nothing contained in these Terms of Use is intended to modify or amend any other agreement currently in effect between you and FOIG.
          </p>
          <p style={styles.text}>
            Although these Terms of Use are intended to apply generally to all content located on this Website, this Website may contain other proprietary notices and conditions of use, the terms of which must also be observed and followed as they apply to any portion of this Website.
          </p>

          <h3 style={styles.sectionTitle}>Other Intellectual Property Rights</h3>
          <p style={styles.text}>
            Please note that any product, process or technology described in the materials contained on this Website may be the subject to other intellectual property rights reserved by FOIG and not specifically licensed hereunder. FOIG, the FOIG logo and other brand names contained on this Website are registered trademarks of FOIG unless otherwise marked as third-party property. Trademarks of other parties are identified wherever possible and FOIG acknowledges their rights to such marks.
          </p>

          <h3 style={styles.sectionTitle}>Disclaimers</h3>
          <p style={styles.text}>
            THE INFORMATION CONTAINED IN THIS WEBSITE IS PROVIDED TO YOU "AS IS," FOR YOUR INTERNAL INFORMATIONAL PURPOSES ONLY, WITHOUT ANY REPRESENTATION OR WARRANTY OF ACCURACY OR COMPLETENESS OF INFORMATION OR OTHER WARRANTY OF ANY KIND, INCLUDING ANY IMPLIED WARRANTY OF QUALITY, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. IN NO EVENT WILL FOIG BE LIABLE TO ANY PARTY FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES FOR USE OF THIS WEBSITE OR RELIANCE UPON ANY INFORMATION OR MATERIAL ACCESSED THROUGH IT OR ANY OTHER HYPERLINKED WEBSITE INCLUDING, BUT NOT LIMITED TO, DAMAGES ARISING FROM LOSS OF PROFITS, BUSINESS INTERRUPTION, OR LOSS OF DATA, EVEN IF FOIG IS EXPRESSLY ADVISED ABOUT THE POSSIBILITY OF SUCH DAMAGES, TO THE FULLEST EXTENT ALLOWABLE BY LAW. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES AND REPRESENTATIONS, THEREFORE THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU.
          </p>
          <p style={styles.text}>
            The material on this Website may contain technical inaccuracies or typographical errors, and such information may be changed, updated, or deleted without prior notice. FOIG may make improvements or changes in the products or services described on this Website at any time. FOIG makes no warranty that this Website will operate uninterrupted or error free or that any defects will be corrected. FOIG does not warrant that this Website is compatible with your computer or other electronic equipment or that this Website or its server is free of errors, viruses, worms or other malware. FOIG shall not be liable for any damages that you may suffer as a result of such issues.
          </p>
          <p style={styles.text}>
            Additionally, FOIG makes no representations or warranties whatsoever about any other website which you may choose to access through this Website. Links provided by FOIG to such third-party websites are provided solely for your convenience and shall not be deemed to imply that FOIG endorses such third-party websites or any content contained therein.
          </p>

          <h3 style={styles.sectionTitle}>Submitting Information to FOI Group, LLC</h3>
          <p style={styles.text}>
            Please see FOIG's Privacy Policy. FOIG does not wish to receive and shall not accept confidential or proprietary information from you through this Website unless FOIG has previously entered a written agreement with you providing for such disclosure. Any information that FOIG receives through this Website, other than your personally identifiable information covered by FOIG's Privacy Policy, will be deemed to be non-confidential. BY TRANSMITTING INFORMATION THROUGH THIS WEBSITE OR ANY OTHER MEANS OF ELECTRONIC COMMUNICATION TO FOIG WITHOUT A PRIOR WRITTEN AGREEMENT WITH FOIG PROVIDING FOR SUCH DISCLOSURE, YOU UNDERSTAND AND AGREE THAT FOIG MAY USE SUCH INFORMATION FOR ANY PURPOSE WHATSOEVER WITHOUT ANY OBLIGATION TO YOU RELATED TO YOUR DISCLOSURE.
          </p>
          <p style={styles.text}>
            FOIG's policy prohibits FOIG and its employees from accepting ideas, suggestions, proposals, product concepts, and the like on a confidential basis.
          </p>

          <p style={styles.effectiveDate}>
            Effective Date: 12 August 2025
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    maxWidth: '900px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px 40px',
    borderBottom: `2px solid #f3f4f6`,
  },
  title: {
    fontSize: '32px',
    fontWeight: fontWeights.bold,
    fontFamily: fonts.primary,
    color: colors.textPrimary,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '40px',
    fontWeight: fontWeights.light,
    color: colors.textSecondary,
    cursor: 'pointer',
    padding: '0',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.3s ease',
  },
  content: {
    padding: '40px',
    overflowY: 'auto',
    flex: 1,
  },
  introText: {
    fontSize: '16px',
    fontWeight: fontWeights.regular,
    fontFamily: fonts.primary,
    color: colors.textSecondary,
    lineHeight: '1.8',
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: fontWeights.bold,
    fontFamily: fonts.primary,
    color: colors.textPrimary,
    marginTop: '30px',
    marginBottom: '15px',
  },
  text: {
    fontSize: '15px',
    fontWeight: fontWeights.regular,
    fontFamily: fonts.primary,
    color: colors.textSecondary,
    lineHeight: '1.8',
    marginBottom: '15px',
  },
  copyrightNotice: {
    fontSize: '15px',
    fontWeight: fontWeights.semibold,
    fontFamily: fonts.primary,
    color: colors.textPrimary,
    lineHeight: '1.8',
    marginBottom: '15px',
    marginLeft: '20px',
    fontStyle: 'italic',
  },
  effectiveDate: {
    fontSize: '14px',
    fontWeight: fontWeights.semibold,
    fontFamily: fonts.primary,
    color: colors.textSecondary,
    marginTop: '40px',
    marginBottom: 0,
  },
};

export default TermsOfUse;